import { useEffect, useMemo, useState } from 'react';
import { pullLocale } from '../lib/locale-sync';
import { Link, NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import {
  LogOut,
  Users,
  Building2,
  LayoutDashboard,
  CreditCard,
  Receipt,
  Bell,
  FileText,
  FileSpreadsheet,
  Calculator,
  BarChart3,
  Target,
  ShieldCheck,
  Wallet,
  HandCoins,
  KeyRound,
  ServerCog,
  CheckCircle2,
  Menu,
  Search,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  HelpCircle,
  Settings,
  UserRound,
  Network,
  Wallet2,
  Banknote,
  UsersRound,
  SlidersHorizontal,
} from 'lucide-react';
import { useAuthStore } from '../stores/auth.store';
import { useAbility } from '../lib/abilities';
import { roleFullLabel } from '../lib/roles';
import { api } from '../lib/api';
import { NpsBanner } from './NpsBanner';
import { ImpersonationBanner } from './ImpersonationBanner';
import { PendingKybBanner } from './PendingKybBanner';
import { Avatar, Badge, CommandPalette, DropdownMenu, Tooltip, type CommandItem } from './ui';
import { NotificationCenter, useUnreadNotificationCount } from './NotificationCenter';
import { SidebarGroup, type SidebarGroupItem } from './SidebarGroup';
import { useKeyboardShortcuts } from '../hooks/useKeyboardShortcuts';
import { cn } from '../lib/cn';

const PILOT_START_DATE = import.meta.env.VITE_PILOT_START_DATE as string | undefined;

/**
 * Sidebar item — role-based visibility:
 *   - `ability`: render edilirken `useAbility().can(action, subject)` true ise göster.
 *   - `hideForRoles`: explicit role blacklist.
 *   - Hiçbiri yoksa herkese görünür.
 */
type NavItem = {
  to: string;
  /** i18n key (relative to "nav" namespace), resolved with t() at render time. */
  label: string;
  icon: typeof LayoutDashboard;
  end?: boolean;
  badge?: boolean;
  ability?: { action: import('../lib/abilities.rules').Action; subject: import('../lib/abilities.rules').Subject };
  hideForRoles?: import('../stores/auth.store').UserRole[];
  ownerOnly?: boolean;
};

/** Standalone (gruplanmamış) üst seviye link'ler — sidebar'ın en üstünde. */
const STANDALONE_NAV: NavItem[] = [
  { to: '/', label: 'sidebar.overview', icon: LayoutDashboard, end: true, badge: false },
  { to: '/alarmlar', label: 'sidebar.alarms', icon: Bell, badge: true, ability: { action: 'read', subject: 'Alarm' } },
];

/**
 * NAV_GROUPS — Sprint 28c sidebar gruplaması.
 * Her grup: stable id (localStorage key), label, ikon, item listesi.
 * Item'lar ability/hideForRoles ile filtrelenir; tüm öğeler gizliyse grup hiç render edilmez.
 */
const NAV_GROUPS: Array<{
  id: string;
  /** i18n key (relative to "nav" namespace), resolved with t() at render time. */
  label: string;
  icon: typeof LayoutDashboard;
  items: NavItem[];
}> = [
  {
    id: 'spending',
    label: 'group.spending',
    icon: Wallet2,
    items: [
      { to: '/kartlar', label: 'sidebar.cards', icon: CreditCard, ability: { action: 'read', subject: 'Card' } },
      {
        to: '/islemler',
        label: 'sidebar.transactions',
        icon: Receipt,
        ability: { action: 'read', subject: 'Transaction' },
      },
      { to: '/fisler', label: 'sidebar.receipts', icon: FileText, ability: { action: 'read', subject: 'Receipt' } },
      {
        to: '/iadeler',
        label: 'sidebar.reimbursements',
        icon: HandCoins,
        ability: { action: 'read', subject: 'Reimbursement' },
      },
      {
        to: '/e-faturalar',
        label: 'sidebar.eInvoices',
        icon: FileSpreadsheet,
        hideForRoles: ['EMPLOYEE', 'EXPENSE_ONLY'],
      },
    ],
  },
  {
    id: 'approvals',
    label: 'group.approvals',
    icon: CheckCircle2,
    items: [
      {
        to: '/onaylar',
        label: 'sidebar.approvals',
        icon: CheckCircle2,
        ability: { action: 'read', subject: 'Approval' },
      },
      {
        to: '/onay-politikalari',
        label: 'sidebar.approvalPolicies',
        icon: SlidersHorizontal,
        ability: { action: 'update', subject: 'ApprovalPolicy' },
      },
    ],
  },
  {
    id: 'finance',
    label: 'group.finance',
    icon: Banknote,
    items: [
      // Cüzdan (founder 2026-07-20): havuz bakiyesi + çalışan fonlaması — Kartlar'dan taşındı.
      { to: '/cuzdan', label: 'sidebar.wallet', icon: Wallet2, ability: { action: 'read', subject: 'Wallet' } },
      { to: '/butceler', label: 'sidebar.budgets', icon: Target, ability: { action: 'read', subject: 'Budget' } },
      { to: '/raporlar', label: 'sidebar.reports', icon: BarChart3, ability: { action: 'read', subject: 'Report' } },
      {
        to: '/muhasebe',
        label: 'sidebar.accounting',
        icon: Calculator,
        hideForRoles: ['EMPLOYEE', 'EXPENSE_ONLY', 'TEAM_LEAD'],
      },
      { to: '/faturalama', label: 'sidebar.billing', icon: Wallet, ability: { action: 'read', subject: 'Billing' } },
    ],
  },
  {
    id: 'team',
    label: 'group.team',
    icon: UsersRound,
    items: [
      // R1 — çalışan/org listesi self-only rollere (EMPLOYEE/EXPENSE_ONLY) gösterilmez.
      // (Bu roller `read:Employee` ability'sine sahip ama ekip görünürlüğü yönetici+ içindir.)
      {
        to: '/calisanlar',
        label: 'sidebar.employees',
        icon: Users,
        ability: { action: 'read', subject: 'Employee' },
        hideForRoles: ['EMPLOYEE', 'EXPENSE_ONLY'],
      },
      {
        to: '/organizasyon',
        label: 'sidebar.organization',
        icon: Network,
        ability: { action: 'read', subject: 'Employee' },
        hideForRoles: ['EMPLOYEE', 'EXPENSE_ONLY'],
      },
      {
        to: '/departmanlar',
        label: 'sidebar.departments',
        icon: Building2,
        ability: { action: 'read', subject: 'Department' },
        hideForRoles: ['EMPLOYEE', 'EXPENSE_ONLY'],
      },
    ],
  },
  {
    id: 'settings',
    label: 'group.settings',
    icon: Settings,
    items: [
      { to: '/sirket', label: 'sidebar.company', icon: Building2, ability: { action: 'read', subject: 'Company' } },
      { to: '/profil', label: 'sidebar.profilePrivacy', icon: ShieldCheck },
      { to: '/ayarlar/bildirimler', label: 'sidebar.notificationPreferences', icon: Bell },
      { to: '/ayarlar/kurumsal-kimlik', label: 'sidebar.corporateIdentity', icon: KeyRound, ownerOnly: true },
      { to: '/admin/yetki-matrisi', label: 'sidebar.permissionMatrix', icon: ShieldCheck, ownerOnly: true },
      { to: '/ayarlar/acil-durum', label: 'sidebar.emergency', icon: ServerCog, ownerOnly: true },
    ],
  },
];

// BRANDBOOK §5.1 — Sidebar item ink bg, krem text. Aktif: 3px lime sol şerit + lime/12 tint.
// `aria-current="page"` NavLink tarafından otomatik set ediliyor (BRANDBOOK §10.5).
const navItemBase =
  'group relative flex items-center gap-2.5 rounded-r-custel-md px-3 py-2 text-sm font-normal text-custel-g300 transition-all duration-custel-fast hover:bg-white/[0.04] hover:text-custel-krem focus:outline-none focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-custel-lime';

const COLLAPSE_KEY = 'custel-sidebar-collapsed';

export function DashboardLayout() {
  // Madde 36 — dil otoritesi sunucu: oturum açılışında `User.locale` varsa arayüzü ona
  // hizala (localStorage yalnız önbellek). Sunucuda değer yoksa dokunulmaz.
  useEffect(() => {
    void pullLocale();
  }, []);

  const { t } = useTranslation('nav');
  const user = useAuthStore((s) => s.user);
  const logout = useAuthStore((s) => s.logout);
  const navigate = useNavigate();
  const location = useLocation();
  const ability = useAbility();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [paletteOpen, setPaletteOpen] = useState(false);
  const [notifOpen, setNotifOpen] = useState(false);
  const unreadCount = useUnreadNotificationCount();
  // BRANDBOOK §5.1 (O2B) — sidebar collapse + localStorage persist
  const [collapsed, setCollapsed] = useState<boolean>(() => {
    if (typeof window === 'undefined') return false;
    try {
      return window.localStorage.getItem(COLLAPSE_KEY) === '1';
    } catch {
      return false;
    }
  });

  useEffect(() => {
    try {
      window.localStorage.setItem(COLLAPSE_KEY, collapsed ? '1' : '0');
    } catch {
      /* ignore storage errors (private mode) */
    }
  }, [collapsed]);

  // Route değişince mobile sidebar'ı kapat
  useMemo(() => setMobileOpen(false), [location.pathname]);

  // Sprint 28c — Sidebar gruplu visibility. Standalone + her grubu ayrı filtrele.
  const itemVisible = (n: NavItem): boolean => {
    if (n.ownerOnly && user?.role !== 'OWNER') return false;
    if (n.hideForRoles && user?.role && n.hideForRoles.includes(user.role)) return false;
    if (n.ability && !ability.can(n.ability.action, n.ability.subject)) return false;
    return true;
  };

  const visibleStandalone = STANDALONE_NAV.filter(itemVisible);
  const visibleGroups = NAV_GROUPS.map((g) => ({
    ...g,
    items: g.items.filter(itemVisible),
  })).filter((g) => g.items.length > 0);

  // Command palette + analytics için flat liste (geriye dönük uyum)
  const flatVisibleNav: NavItem[] = [...visibleStandalone, ...visibleGroups.flatMap((g) => g.items)];

  const alarmsCount = useQuery({
    queryKey: ['alarms-unack-count'],
    // Alarm yetkisi yoksa polling yapma — yetkisiz olduğu için 403 spam'i önler.
    enabled: ability.can('read', 'Alarm'),
    queryFn: () => api.get<{ count: number }>('/alarms/unacknowledged-count'),
    refetchInterval: 30_000,
  });

  async function handleLogout() {
    await logout();
    navigate('/giris');
  }

  // Cmd+K — komut paleti
  useKeyboardShortcuts([
    {
      key: 'k',
      meta: true,
      handler: () => setPaletteOpen(true),
      description: t('topbar.openCommandPalette'),
      allowInInputs: true,
    },
  ]);

  const paletteItems: CommandItem[] = [
    ...flatVisibleNav.map((n) => ({
      id: `nav-${n.to}`,
      label: t(n.label),
      keywords: [n.to.replace(/^\//, '')],
      category: t('palette.pages'),
      icon: <n.icon size={14} />,
      onSelect: () => navigate(n.to),
    })),
    {
      id: 'action-logout',
      label: t('palette.logout'),
      category: t('palette.action'),
      icon: <LogOut size={14} />,
      onSelect: () => void handleLogout(),
    },
  ];

  // BRANDBOOK §5.1 — Sidebar (ink bg, krem/g300 text, lime aktif stripe).
  // Daraltılmış (collapsed) modda label gizli, icon ortalı, tooltip ile menu adı.
  const sidebar = (
    <>
      <div className="flex items-center justify-between border-b border-white/[0.08] px-4 py-4">
        <Link
          to="/"
          className="flex items-center gap-2.5 rounded-custel-sm focus:outline-none focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-custel-lime"
          aria-label={t('sidebar.homeAria')}
          title={collapsed ? t('sidebar.homeAria') : undefined}
        >
          {/* BRANDBOOK §5.1 (C1C) — sidebar logo: 28×28 Custel ikon (lime kare + ink C).
              SVG handoff/brand/app-icons/source/custel-icon.svg'den public/'e kopyalandı. */}
          <img
            src="/custel-icon.svg"
            alt=""
            aria-hidden="true"
            width={28}
            height={28}
            className="h-7 w-7 shrink-0 rounded-custel-md"
          />
          {!collapsed && (
            <span className="min-w-0">
              <span className="block text-[15px] font-semibold leading-tight tracking-tight text-custel-krem">
                Custel
              </span>
              <span className="block truncate text-[11px] leading-tight text-custel-g500">
                {user?.company.legalName ?? '—'}
              </span>
            </span>
          )}
        </Link>
        {/* Desktop collapse toggle (mobile drawer'da gizli). */}
        <button
          type="button"
          onClick={() => setCollapsed((v) => !v)}
          aria-label={collapsed ? t('sidebar.expandMenu') : t('sidebar.collapseMenu')}
          className="hidden h-6 w-6 shrink-0 items-center justify-center rounded text-custel-g500 transition-colors hover:bg-white/[0.06] hover:text-custel-krem focus:outline-none focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-custel-lime md:flex"
        >
          {collapsed ? <ChevronRight size={14} aria-hidden="true" /> : <ChevronLeft size={14} aria-hidden="true" />}
        </button>
      </div>

      <nav className="flex-1 overflow-y-auto p-2">
        {/* Sprint 28c — Standalone (gruplanmamış) öğeler: Genel Bakış + Alarmlar */}
        {visibleStandalone.map(({ to, label, icon: Icon, end, badge }) => (
          <NavLink
            key={to}
            to={to}
            end={end}
            title={collapsed ? t(label) : undefined}
            className={({ isActive }) =>
              cn(
                navItemBase,
                isActive && 'bg-custel-lime/[0.12] font-medium text-custel-krem',
                collapsed && 'justify-center px-2',
              )
            }
          >
            {({ isActive }) => (
              <>
                {/* BRANDBOOK §5.1 — aktif öğenin 3px lime sol şeridi */}
                {isActive && (
                  <span aria-hidden="true" className="absolute inset-y-1.5 left-0 w-[3px] rounded-r bg-custel-lime" />
                )}
                <Icon size={16} aria-hidden="true" />
                {!collapsed && <span className="flex-1 truncate">{t(label)}</span>}
                {!collapsed && badge && alarmsCount.data && alarmsCount.data.count > 0 && (
                  <Badge
                    shape="label"
                    tone="accent"
                    size="sm"
                    aria-label={t('sidebar.unreadAlarms', { count: alarmsCount.data.count })}
                  >
                    {alarmsCount.data.count}
                  </Badge>
                )}
              </>
            )}
          </NavLink>
        ))}

        {/* Sprint 28c — Collapsible gruplar (Harcama / Onay / Finans / Ekip / Ayarlar) */}
        <div className="mt-2 space-y-0.5">
          {visibleGroups.map((g) => {
            const items: SidebarGroupItem[] = g.items.map((it) => ({
              to: it.to,
              label: t(it.label),
              icon: it.icon,
              end: it.end,
            }));
            return (
              <SidebarGroup key={g.id} id={g.id} label={t(g.label)} icon={g.icon} items={items} collapsed={collapsed} />
            );
          })}
        </div>
      </nav>

      {/* ThemeToggle gizlendi — Sprint 28c (BRANDBOOK §2.1, light-only). Faz 4+'da geri açılabilir. */}
    </>
  );

  return (
    <div className="flex min-h-screen bg-custel-krem">
      {/* Desktop sidebar — BRANDBOOK §5.1 (240px/64px, ink bg) */}
      <aside
        className={cn(
          'sticky top-0 hidden h-screen shrink-0 flex-col bg-custel-ink text-custel-krem transition-[width] duration-custel-base md:flex',
          collapsed ? 'w-16' : 'w-60',
        )}
        aria-label={t('sidebar.primaryNav')}
      >
        {sidebar}
      </aside>

      {/* Mobile sidebar drawer */}
      {mobileOpen && (
        <>
          <button
            type="button"
            aria-label={t('sidebar.closeMenu')}
            onClick={() => setMobileOpen(false)}
            className="fixed inset-0 z-40 md:hidden"
            style={{ backgroundColor: 'var(--custel-overlay)' }}
          />
          <aside className="fixed inset-y-0 left-0 z-50 flex w-72 flex-col bg-custel-ink text-custel-krem shadow-custel-drawer md:hidden">
            {sidebar}
          </aside>
        </>
      )}

      <div className="flex min-w-0 flex-1 flex-col">
        <ImpersonationBanner />
        <PendingKybBanner />
        <NpsBanner pilotStartDate={PILOT_START_DATE} />
        {/* BRANDBOOK §5.2 — TopBar 56px, paper bg, border-bottom g200, wordmark + search + bell + avatar */}
        <header className="sticky top-0 z-30 flex h-14 items-center justify-between gap-3 border-b border-custel-border bg-custel-paper px-4 md:px-6">
          <div className="flex flex-1 items-center gap-3">
            <button
              type="button"
              onClick={() => setMobileOpen(true)}
              aria-label={t('sidebar.openMenu')}
              className="rounded-custel-md p-1 text-custel-g500 hover:bg-custel-g100 hover:text-custel-ink focus:outline-none focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-custel-ink md:hidden"
            >
              <Menu size={20} aria-hidden="true" />
            </button>
            {/* Wordmark (mobile + collapsed sidebar fallback) */}
            <Link
              to="/"
              className="font-semibold tracking-tight text-custel-ink transition-colors hover:text-custel-g700"
              aria-label={t('sidebar.homeAria')}
              style={{ fontSize: 18, letterSpacing: '-0.02em' }}
            >
              Custel
            </Link>
            <Tooltip content={t('topbar.commandPaletteTooltip')}>
              <button
                type="button"
                onClick={() => setPaletteOpen(true)}
                className="ml-2 hidden h-9 max-w-[380px] flex-1 items-center gap-2 rounded-custel-md bg-custel-g100 px-3 text-[13px] text-custel-g500 transition-colors hover:bg-custel-g150 hover:text-custel-g700 focus:outline-none focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-custel-ink sm:inline-flex"
                aria-label={t('topbar.commandPalette')}
              >
                <Search size={14} aria-hidden="true" />
                <span className="flex-1 text-left">{t('topbar.searchPlaceholder')}</span>
                <kbd className="rounded border border-custel-border bg-custel-paper px-1.5 py-0.5 font-mono text-[10px] font-medium text-custel-g500">
                  ⌘K
                </kbd>
              </button>
            </Tooltip>
          </div>

          <div className="flex items-center gap-1">
            <Tooltip content={t('topbar.notifications')}>
              <button
                type="button"
                onClick={() => setNotifOpen(true)}
                aria-label={
                  unreadCount > 0 ? t('topbar.unreadNotifications', { count: unreadCount }) : t('topbar.notifications')
                }
                className="relative inline-flex h-9 w-9 items-center justify-center rounded-custel-md text-custel-g500 transition-colors hover:bg-custel-g100 hover:text-custel-ink focus:outline-none focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-custel-ink"
              >
                <Bell size={18} aria-hidden="true" />
                {unreadCount > 0 && (
                  <span className="absolute -right-0.5 -top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-custel-lime px-1 font-mono text-[10px] font-medium text-custel-ink">
                    {unreadCount > 9 ? '9+' : unreadCount}
                  </span>
                )}
              </button>
            </Tooltip>
            <Tooltip content={t('topbar.help')}>
              <button
                type="button"
                onClick={() => navigate('/profil')}
                aria-label={t('topbar.help')}
                className="inline-flex h-9 w-9 items-center justify-center rounded-custel-md text-custel-g500 transition-colors hover:bg-custel-g100 hover:text-custel-ink focus:outline-none focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-custel-ink"
              >
                <HelpCircle size={18} aria-hidden="true" />
              </button>
            </Tooltip>

            <DropdownMenu
              trigger={
                <span className="flex items-center gap-2 rounded-custel-md p-1 transition-colors hover:bg-custel-g100 focus:outline-none focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-custel-ink">
                  <Avatar name={user?.fullName ?? ''} size="md" current />
                  <span className="hidden text-left sm:block">
                    <span className="block text-sm font-medium leading-tight text-custel-ink">
                      {user?.fullName ?? '—'}
                    </span>
                    <span className="block text-[11px] leading-tight text-custel-text-tertiary">
                      {roleFullLabel(user?.role)}
                    </span>
                  </span>
                  <ChevronDown size={14} className="hidden text-custel-text-tertiary sm:inline" aria-hidden="true" />
                </span>
              }
              items={[
                {
                  label: t('sidebar.profilePrivacy'),
                  icon: <UserRound size={14} aria-hidden="true" />,
                  onSelect: () => navigate('/profil'),
                },
                {
                  label: t('menu.companySettings'),
                  icon: <Settings size={14} aria-hidden="true" />,
                  onSelect: () => navigate('/sirket'),
                },
                { separator: true, label: '' },
                {
                  label: t('menu.logout'),
                  icon: <LogOut size={14} aria-hidden="true" />,
                  destructive: true,
                  onSelect: () => void handleLogout(),
                },
              ]}
              menuWidth="min-w-[14rem]"
            />
          </div>
        </header>

        <main className="flex-1 overflow-auto bg-custel-krem p-4 md:p-6">
          <Outlet />
        </main>
      </div>

      <CommandPalette
        open={paletteOpen}
        onClose={() => setPaletteOpen(false)}
        items={paletteItems}
        enableServerSearch
      />
      <NotificationCenter open={notifOpen} onClose={() => setNotifOpen(false)} />
    </div>
  );
}
